import axios from "axios";
import { mailgunConfig, dnApiConfig, financialApiConfig, newsletterApiConfig } from "../config/api.config";
import { auth } from "../utils/firebase";
import { db } from "../utils/firebase";
import { collection, doc, onSnapshot, getDoc, Unsubscribe } from "firebase/firestore";
import { EmailBatch } from "../types/logging";

/**
 * Email types
 */
export enum EmailType {
  FINANCIAL_REPORT = "financial",
  NEWSLETTER = "newsletter",
  OUTREACH = "outreach",
}

/**
 * Options for initiating a batch send request
 */
export interface BatchSendOptions {
  subject: string;
  message_greeting: string;
  message_body: string;
  emailType: 'financial' | 'newsletter';
  month?: string;  // Required for financial emails
  year?: string;   // Required for financial emails
}

/**
 * Response from successful batch send initiation
 */
export interface BatchSendResponse {
  success: boolean;
  batchId: string;
  totalRecipients: number;
  status: 'queued' | 'in_progress' | 'completed' | 'failed';
}

/**
 * Real-time progress update for an active batch
 */
export interface BatchProgress {
  sent: number;
  failed: number;
  total: number;
  percent: number;
  status: 'queued' | 'in_progress' | 'completed' | 'failed';
}

/**
 * Base email options interface
 */
export interface EmailOptions {
  recipients: Array<{ email: string; name?: string }>;
  subject: string;
  body: string;
  greeting?: string;
  testMode?: boolean;
  testEmail?: string;
  overrideToAddress?: string;
}

/**
 * Financial report specific options
 */
export interface FinancialReportOptions extends EmailOptions {
  month: string;
  year: string;
  customIds?: string[];
}

/**
 * Newsletter specific options
 */
export interface NewsletterOptions extends EmailOptions {
  customIds?: string[];
}

/**
 * Outreach specific options
 */
export interface OutreachOptions extends EmailOptions {
  campaignId?: string;
  trackingEnabled?: boolean;
}

/**
 * Result of sending an email
 */
export interface SendResult {
  success: boolean;
  message: string;
  data?: any;
}

const FINANCIAL_LOG_LOOKBACK_DAYS = 10;

const getFinancialApiUrl = (path?: string): string => {
  const baseUrl = (financialApiConfig.baseUrl || "").trim();
  if (!baseUrl) {
    throw new Error("Financial API Gateway URL is not configured");
  }

  const normalizedBase = baseUrl.replace(/\/+$/, "");
  if (!path) {
    return normalizedBase;
  }

  const trimmedPath = path.trim();
  const normalizedPath = trimmedPath.startsWith("/")
    ? trimmedPath
    : `/${trimmedPath}`;

  return `${normalizedBase}${normalizedPath}`;
};

const getAuthTokenOrThrow = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated");
  }

  return user.getIdToken();
};

/**
 * Send an email via the API endpoint
 *
 * @param options Email options
 * @returns Send result
 */
export const sendViaAPI = async (options: any): Promise<SendResult> => {
  try {
    const response = await axios.post(
      "https://cjed05n28l.execute-api.us-east-1.amazonaws.com/staging/send-mail",
      options,
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": dnApiConfig.apiKey,
        },
      }
    );

    return {
      success: true,
      message: `Email sent successfully to ${options.recipient}`,
      data: response.data,
    };
  } catch (error) {
    console.error("Error sending email via API:", error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Unknown error sending email",
    };
  }
};

/**
 * Send an email via Mailgun
 *
 * @param options Email options
 * @returns Send result
 */
export const sendViaMailgun = async (
  options: EmailOptions
): Promise<SendResult> => {
  try {
    const {
      recipients,
      subject,
      body,
      overrideToAddress,
      testMode,
      testEmail,
    } = options;

    if (recipients.length === 0 && !overrideToAddress && !testEmail) {
      return {
        success: false,
        message: "No recipients provided",
      };
    }

    // Determine the recipient list based on priority:
    // 1. If overrideToAddress is provided, use it
    // 2. If in test mode with testEmail, use testEmail
    // 3. Otherwise use the recipients list
    let to = recipients.map((r) => r.email).join(",");

    if (overrideToAddress && overrideToAddress.trim() !== "") {
      to = overrideToAddress;
    } else if (testMode && testEmail) {
      to = testEmail;
    }

    // Prepare the data for Mailgun API
    const formData = new URLSearchParams();
    formData.append("from", `Distro Nation <noreply@${mailgunConfig.domain}>`);
    formData.append("to", to);
    formData.append("subject", subject);
    formData.append("html", body);

    // If in test mode, add a header to indicate it's a test
    if (testMode) {
      formData.append("h:X-Mailgun-Tag", "Test");
    }

    // Make the API request to Mailgun
    const response = await axios({
      method: "post",
      url: `https://api.mailgun.net/v3/${mailgunConfig.domain}/messages`,
      auth: {
        username: "api",
        password: mailgunConfig.apiKey,
      },
      data: formData,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    // Email sent successfully

    // Determine the success message based on what was used
    let successMessage = "";
    if (testMode && testEmail) {
      successMessage = `Test email sent to ${testEmail}`;
    } else if (overrideToAddress && overrideToAddress.trim() !== "") {
      successMessage = `Email sent to override address: ${overrideToAddress}`;
    } else {
      successMessage = `Email sent to ${recipients.length} recipient(s)`;
    }

    return {
      success: true,
      message: successMessage,
      data: response.data,
    };
  } catch (error) {
    console.error("Error sending email via Mailgun:", error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Unknown error sending email",
    };
  }
};

/**
 * Send a financial report email
 *
 * @param options Financial report options
 * @returns Send result
 */
export const sendFinancialReport = async (
  options: FinancialReportOptions
): Promise<SendResult> => {
  try {
    const {
      recipients,
      subject,
      body,
      greeting,
      month,
      year,
      customIds,
      testEmail,
      overrideToAddress,
    } = options;

    const normalizedRecipients =
      recipients?.map((recipient) => recipient.email).filter(Boolean) ?? [];
    const overrideRecipient = (testEmail || overrideToAddress)?.trim();

    if (normalizedRecipients.length === 0 && !overrideRecipient) {
      return {
        success: false,
        message: "No recipients provided",
      };
    }

    const normalizedSubject = subject?.trim();
    if (!normalizedSubject) {
      throw new Error("Subject is required for financial reports");
    }

    const normalizedMonth = month?.trim();
    const normalizedYear = year?.trim();
    if (!normalizedMonth || !normalizedYear) {
      throw new Error("Both month and year are required for financial reports");
    }

    const sanitizedCustomIds =
      customIds?.map((id) => id.trim()).filter(Boolean) ?? [];

    const payload = {
      to: overrideRecipient || normalizedRecipients,
      subject: normalizedSubject,
      message_greeting: greeting || "Hello",
      message_body: body,
      month: normalizedMonth,
      year: normalizedYear,
      customIds: sanitizedCustomIds,
      attachReporting: sanitizedCustomIds.length > 0,
      tags: [
        "financial-report",
        `month-${normalizedMonth}`,
        `year-${normalizedYear}`,
      ],
      campaignId: `financial_${normalizedMonth}_${normalizedYear}_${Date.now()}`,
    };

    const apiEndpoint = getFinancialApiUrl(
      financialApiConfig.sendReportPath || "/financial/send-report"
    );
    const token = await getAuthTokenOrThrow();

    const response = await axios.post(apiEndpoint, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      success: true,
      message:
        response.data?.message || "Financial report sent successfully",
      data: response.data?.data ?? response.data,
    };
  } catch (error) {
    console.error("Error sending financial report:", error);
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Unknown error sending financial report",
    };
  }
};

/**
 * Send a newsletter email via SES
 *
 * @param options Newsletter options
 * @returns Send result
 */
export const sendNewsletter = async (
  options: NewsletterOptions
): Promise<SendResult> => {
  try {
    const {
      recipients,
      subject,
      body,
      greeting,
      customIds,
      testMode,
      testEmail,
      overrideToAddress,
    } = options;

    // Validate recipients
    const normalizedRecipients =
      recipients?.map((recipient) => recipient.email).filter(Boolean) ?? [];
    const overrideRecipient = (testEmail || overrideToAddress)?.trim();

    if (normalizedRecipients.length === 0 && !overrideRecipient) {
      return {
        success: false,
        message: "No recipients provided",
      };
    }

    const normalizedSubject = subject?.trim();
    if (!normalizedSubject) {
      throw new Error("Subject is required for newsletters");
    }

    // Prepare payload for Newsletter SES Lambda
    const payload = {
      recipient: overrideRecipient || normalizedRecipients[0], // Support single recipient for now
      to: overrideRecipient || normalizedRecipients, // Also send array for future batch support
      subject: normalizedSubject,
      message_greeting: greeting || "Hello",
      message_body: body, // Rich HTML from ReactQuill
      tags: ["newsletter", testMode ? "test-mode" : "production"],
      from: "Distro Nation <distro-nation@distro-nation.com>",
    };

    // Get Newsletter API endpoint
    const apiEndpoint = `${newsletterApiConfig.baseUrl}${newsletterApiConfig.sendPath}`;

    // Get Firebase auth token
    const token = await getAuthTokenOrThrow();

    console.log("Sending newsletter via SES:", {
      endpoint: apiEndpoint,
      recipientCount: Array.isArray(payload.to) ? payload.to.length : 1,
      testMode,
    });

    // Call Newsletter SES Lambda
    const response = await axios.post(apiEndpoint, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      success: true,
      message:
        response.data?.message || "Newsletter sent successfully via SES",
      data: response.data?.data ?? response.data,
    };
  } catch (error) {
    console.error("Error sending newsletter via SES:", error);
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Unknown error sending newsletter via SES",
    };
  }
};

/**
 * Send an outreach email
 *
 * @param options Outreach options
 * @returns Send result
 */
export const sendOutreachEmail = async (
  options: OutreachOptions
): Promise<SendResult> => {
  try {
    const {
      recipients,
      subject,
      body,
      testMode,
      testEmail,
      overrideToAddress,
    } = options;

    // Use Mailgun for outreach emails
    return sendViaMailgun({
      recipients,
      subject,
      body,
      testMode,
      testEmail,
      overrideToAddress,
    });
  } catch (error) {
    console.error("Error sending outreach email:", error);
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Unknown error sending outreach email",
    };
  }
};

/**
 * Mailgun event data interface
 */
export interface MailgunEvent {
  event: string;
  timestamp: number;
  message: {
    headers: {
      "message-id": string;
      from: string;
      to: string;
      subject: string;
    };
  };
  recipient?: string;
  "recipient-domain"?: string;
  "delivery-status"?: {
    message?: string;
    code?: number;
  };
  reason?: string;
  description?: string;
}

/**
 * Processed email log entry for UI display
 */
export interface EmailLogEntry {
  id: string;
  event: string;
  timestamp: Date;
  recipient: string;
  subject: string;
  provider?: "ses";
  status:
    | "delivered"
    | "failed"
    | "opened"
    | "clicked"
    | "bounced"
    | "complained"
    | "other";
  description?: string;
}

const getSESLogs = async (): Promise<EmailLogEntry[]> => {
  try {
    const token = await getAuthTokenOrThrow();
    const apiEndpoint = getFinancialApiUrl(
      financialApiConfig.trackingDataPath || "/financial/tracking-data"
    );

    const now = new Date();
    const startDate = new Date(
      now.getTime() - FINANCIAL_LOG_LOOKBACK_DAYS * 24 * 60 * 60 * 1000
    );

    const response = await axios.get(apiEndpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate: startDate.toISOString(),
        endDate: now.toISOString(),
        limit: 100,
      },
    });

    const items = response?.data?.data?.items ?? [];
    return items
      .map((record: any) => mapFinancialRecordToEmailLog(record))
      .filter(
        (entry: EmailLogEntry | null): entry is EmailLogEntry => Boolean(entry)
      );
  } catch (error) {
    console.error("Error fetching SES logs:", error);
    return [];
  }
};

const mapFinancialRecordToEmailLog = (
  record: any
): EmailLogEntry | null => {
  if (!record) {
    return null;
  }

  const timestampSource =
    record.timestampIso ||
    record.sesEventTimestamp ||
    record.timestamp ||
    record.createdAt ||
    record.lastUpdated;

  const timestamp = timestampSource ? new Date(timestampSource) : new Date();
  if (Number.isNaN(timestamp.getTime())) {
    timestamp.setTime(Date.now());
  }

  const subjectParts = ["Financial Report"];
  if (record.monthLabel || record.month) {
    subjectParts.push(record.monthLabel || record.month);
  }
  if (record.year) {
    subjectParts.push(record.year);
  }

  return {
    id:
      record.messageId ||
      record.sesMessageId ||
      record.eventId ||
      `ses-${timestamp.getTime()}`,
    event: record.sesEventType || record.eventType || "financial",
    timestamp,
    recipient: record.recipient || record.email || "Unknown recipient",
    subject: subjectParts.join(" - "),
    provider: "ses",
    status: mapSESEventToStatus(record.sesEventType, record.sendStatus),
    description: generateSESEventDescription(record),
  };
};

const mapSESEventToStatus = (
  eventType?: string,
  sendStatus?: string
): EmailLogEntry["status"] => {
  switch ((eventType || "").toUpperCase()) {
    case "DELIVERY":
    case "DELIVERED":
      return "delivered";
    case "BOUNCE":
      return "bounced";
    case "COMPLAINT":
      return "complained";
    case "OPEN":
      return "opened";
    case "CLICK":
      return "clicked";
    default: {
      const normalizedStatus = (sendStatus || "").toLowerCase();
      if (normalizedStatus === "failed") {
        return "failed";
      }
      return "other";
    }
  }
};

const generateSESEventDescription = (record: any): string => {
  const eventType = (record?.sesEventType || record?.eventType || "").toUpperCase();
  switch (eventType) {
    case "DELIVERY":
    case "DELIVERED":
      return "Email delivered successfully via SES";
    case "BOUNCE":
      return `Email bounced: ${
        record?.sesEventDetail?.bounceType ||
        record?.sendError ||
        "Unknown reason"
      }`;
    case "COMPLAINT":
      return "Recipient marked email as spam";
    case "OPEN":
      return "Email was opened by recipient";
    case "CLICK":
      return "Recipient clicked a link in the email";
    default:
      if (record?.sendError) {
        return record.sendError;
      }
      if (record?.sendStatus) {
        return `Email ${record.sendStatus.toLowerCase()}`;
      }
      return `Email ${eventType ? eventType.toLowerCase() : "event"}`;
  }
};

export const getEmailLogs = async (): Promise<EmailLogEntry[]> => {
  try {
    return getSESLogs();
  } catch (error) {
    console.error("Error fetching email logs:", error);
    return [];
  }
};

/**
 * Send an email based on the email type
 *
 * @param type Email type
 * @param options Email options
 * @returns Send result
 */
export const sendEmail = async (
  type: EmailType,
  options: any
): Promise<SendResult> => {
  switch (type) {
    case EmailType.FINANCIAL_REPORT:
      return sendFinancialReport(options as FinancialReportOptions);
    case EmailType.NEWSLETTER:
      return sendNewsletter(options as NewsletterOptions);
    case EmailType.OUTREACH:
      return sendOutreachEmail(options as OutreachOptions);
    default:
      return {
        success: false,
        message: `Unknown email type: ${type}`,
      };
  }
};

/**
 * Initiate a batch email send via the /financial/send-batch-report endpoint
 * This function triggers the SQS-based asynchronous batch processing pipeline.
 *
 * @param options Batch send options with email configuration
 * @returns Promise with batch send response containing batchId and initial status
 * @throws Error if authentication fails or API call fails
 */
export const startBatchSend = async (
  options: BatchSendOptions
): Promise<BatchSendResponse> => {
  try {
    // Validate required fields
    if (!options.subject || !options.message_greeting || !options.message_body || !options.emailType) {
      throw new Error("Missing required batch send options: subject, message_greeting, message_body, emailType");
    }

    // Get Firebase JWT token from current user
    const token = await getAuthTokenOrThrow();

    // Build the API endpoint URL
    const apiEndpoint = getFinancialApiUrl(
      financialApiConfig.batchSendPath || "/financial/send-batch-report"
    );

    // Prepare request payload
    const payload: BatchSendOptions = {
      subject: options.subject,
      message_greeting: options.message_greeting,
      message_body: options.message_body,
      emailType: options.emailType,
      ...(options.month && { month: options.month }),
      ...(options.year && { year: options.year }),
    };

    // Make POST request with Firebase JWT authentication
    const response = await axios.post(apiEndpoint, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    // Parse and return the batch response
    const batchResponse: BatchSendResponse = {
      success: response.data.success !== false,
      batchId: response.data.batchId,
      totalRecipients: response.data.totalRecipients || 0,
      status: response.data.status || "queued",
    };

    return batchResponse;
  } catch (error) {
    // Handle specific HTTP error codes
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      const errorData = error.response.data;

      switch (status) {
        case 401:
          // Authentication error - attempt token refresh
          console.warn("Batch send authentication failed (401). Attempting token refresh...");
          try {
            const user = auth.currentUser;
            if (!user) {
              throw new Error("User not authenticated. Please log in again.");
            }
            // Force token refresh
            const refreshedToken = await user.getIdToken(true);
            console.log("Token refreshed successfully. Retrying batch send...");
            // Optionally retry the request with refreshed token
            // For now, we'll throw the error so the caller can retry
            throw new Error("Authentication failed. Please try again.");
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
            throw new Error("Authentication expired. Please log in again.");
          }

        case 400:
          // Validation error - typically bad request parameters
          const validationMessage = errorData?.message || 
            errorData?.error || 
            "Invalid batch send parameters. Please check your input.";
          console.error("Batch send validation error:", errorData);
          throw new Error(`Validation error: ${validationMessage}`);

        case 500:
          // Server error
          const serverErrorMessage = errorData?.message || 
            errorData?.error || 
            "Server error processing batch send. Please try again later.";
          console.error("Batch send server error:", errorData);
          throw new Error(`Server error: ${serverErrorMessage}`);

        default:
          // Other HTTP errors
          console.error(`Batch send HTTP error ${status}:`, errorData);
          throw new Error(`HTTP ${status}: ${errorData?.message || "Failed to initiate batch send"}`);
      }
    }

    // Handle network errors or other non-axios errors
    if (error instanceof Error) {
      console.error("Batch send error:", error.message);
      throw error;
    }

    // Unknown error
    console.error("Unknown error during batch send:", error);
    throw new Error("Unknown error initiating batch send");
  }
};

/**
 * Subscribe to real-time batch progress updates using Firestore
 * This function sets up a listener on the emailBatches document that fires
 * whenever the batch progress changes (sentCount, failedCount, status).
 *
 * @param batchId The ID of the batch to monitor
 * @param onUpdate Callback fired on every document update with current progress
 * @param onComplete Callback fired when batch reaches 'completed' status
 * @returns An unsubscribe function that removes the listener (for cleanup)
 */
export const subscribeToBatchProgress = (
  batchId: string,
  onUpdate: (progress: BatchProgress) => void,
  onComplete: (batch: EmailBatch) => void
): Unsubscribe => {
  try {
    // Create reference to the specific batch document
    const batchDocRef = doc(collection(db, "emailBatches"), batchId);

    // Set up real-time listener using Firestore onSnapshot
    const unsubscribe = onSnapshot(
      batchDocRef,
      (docSnapshot) => {
        console.log(`[Firestore] Received update for batch ${batchId}`, {
          exists: docSnapshot.exists(),
          metadata: docSnapshot.metadata,
        });

        if (docSnapshot.exists()) {
          const batchData = docSnapshot.data() as EmailBatch;
          
          console.log(`[Firestore] Batch data:`, {
            sentCount: batchData.sentCount,
            failedCount: batchData.failedCount,
            totalRecipients: batchData.totalRecipients,
            status: batchData.status,
          });

          // Calculate progress percentage
          const processedCount = batchData.sentCount + batchData.failedCount;
          const percent = (processedCount / batchData.totalRecipients) * 100;

          // Create progress object for callback
          const progress: BatchProgress = {
            sent: batchData.sentCount,
            failed: batchData.failedCount,
            total: batchData.totalRecipients,
            percent: Math.round(percent * 100) / 100, // Round to 2 decimal places
            status: batchData.status,
          };

          console.log(`[Firestore] Calling onUpdate with progress:`, progress);

          // Fire onUpdate callback with current progress
          onUpdate(progress);

          // Check if batch is complete
          if (batchData.status === "completed") {
            console.log(`Batch ${batchId} completed. Sent: ${batchData.sentCount}, Failed: ${batchData.failedCount}`);
            // Fire onComplete callback with full batch data
            onComplete(batchData);
          }
        } else {
          console.warn(`Batch document ${batchId} not found in Firestore`);
        }
      },
      (error) => {
        console.error(`Error listening to batch progress for ${batchId}:`, error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error("Error setting up batch progress subscription:", error);
    // Return a no-op unsubscribe function if setup fails
    return () => {};
  }
};

/**
 * Fetch current batch status from Firestore (one-time fetch, not real-time)
 * Use as a polling fallback if real-time listener doesn't work
 */
export const fetchBatchProgress = async (
  batchId: string
): Promise<BatchProgress | null> => {
  try {
    const batchDocRef = doc(collection(db, "emailBatches"), batchId);
    const docSnapshot = await getDoc(batchDocRef);

    if (docSnapshot.exists()) {
      const batchData = docSnapshot.data() as EmailBatch;

      console.log(`[fetchBatchProgress] Batch ${batchId} data:`, {
        sentCount: batchData.sentCount,
        failedCount: batchData.failedCount,
        totalRecipients: batchData.totalRecipients,
        status: batchData.status,
      });

      // Calculate progress percentage
      const processedCount = batchData.sentCount + batchData.failedCount;
      const percent = (processedCount / batchData.totalRecipients) * 100;

      return {
        sent: batchData.sentCount,
        failed: batchData.failedCount,
        total: batchData.totalRecipients,
        percent: Math.round(percent * 100) / 100,
        status: batchData.status,
      };
    }

    console.warn(`[fetchBatchProgress] Batch ${batchId} not found in Firestore`);
    return null;
  } catch (error) {
    console.error(`Error fetching batch progress for ${batchId}:`, error);
    return null;
  }
};
